/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/naming-convention */
import { useQueries } from "@tanstack/react-query";
import dayjs from "dayjs";

import type { ICategory } from "app/common/services/categoryService.types";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import type {
    IMetricData,
    IMetrics,
    IRawData,
    IUniqueUsersOvertimeRawData,
    IUniqueUsersAverageRawData,
} from "app/pages/industry-analysis/overview/utils/types";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import { TRAFFIC_AND_ENGAGEMENT_ENDPOINTS, UNIQUE_USERS_ENDPOINTS } from "../consts";

import { useDurationValidationRecovery } from "./useDurationValidationRecovery";

export type HighLevelMetricsParams = {
    duration?: string;
    country?: string;
    webSource?: string;
    comparedDuration?: string;
    includeSubdomains?: boolean;
};

/**
 * Normalizes Graph data to handle both API response shapes:
 * 1. Standard: { Desktop: IMetrics, Total?: IMetrics }
 * 2. Nested: { Total: IMetrics } (where Total contains the actual metrics)
 */
const normalizeGraphData = (data: IRawData): IRawData => {
    if (!data.Graph) {
        return data;
    }

    const graphKeys = Object.keys(data.Graph);

    if (
        graphKeys.length === 1 &&
        graphKeys[0] === "Total" &&
        data.Graph.Total &&
        typeof data.Graph.Total === "object"
    ) {
        return {
            ...data,
            Graph: { Total: data.Graph.Total } as IRawData["Graph"],
        };
    }

    return data;
};

const mergeUniqueUsersData = (
    trafficData: IRawData,
    uniqueUsersOvertime: IUniqueUsersOvertimeRawData,
    uniqueUsersAverage: IUniqueUsersAverageRawData,
    isPop: boolean,
): IRawData => {
    const mergedAverage = {
        ...trafficData.Average,
        ...(uniqueUsersAverage.Data[0] && { UniqueUsers: uniqueUsersAverage.Data[0].UniqueUsers }),
    };

    const mergedGraph = Object.fromEntries(
        Object.entries(trafficData.Graph).map(([metric, metricData]) => [
            metric,
            {
                ...metricData,
                UniqueUsers: isPop
                    ? Object.values(uniqueUsersOvertime?.Data)?.[0][metric]
                    : {
                          Monthly: { Graph: Object.values(uniqueUsersOvertime.Data)[0]?.[metric]?.[0] },
                      },
            },
        ]),
    );

    return {
        ...trafficData,
        Average: mergedAverage,
        Graph: mergedGraph as IRawData["Graph"],
    };
};

const isMetricData = (value: IMetrics[string]): value is IMetricData => !!value && "Weekly" in value;

/**
 * Walks Graph.[device].[metric].Weekly.Graph arrays and adds PeriodEnd to each data point.
 * Non-last points: PeriodEnd = day before the next point's Key.
 * Last point: PeriodEnd = the inclusive toDate.
 */
const enrichWeeklyDataWithPeriodEnd = (data: IRawData, toDate: string): IRawData => {
    if (!data.Graph) {
        return data;
    }

    const lastDay = dayjs.utc(toDate).format("YYYY-MM-DD");

    const enrichedGraph = Object.fromEntries(
        Object.entries(data.Graph).map(([deviceKey, metrics]) => {
            if (!metrics) {
                return [deviceKey, metrics];
            }

            const enrichedMetrics = Object.fromEntries(
                Object.entries(metrics).map(([metricKey, metricData]) => {
                    if (!isMetricData(metricData)) {
                        return [metricKey, metricData];
                    }

                    const points = metricData.Weekly.Graph;
                    return [
                        metricKey,
                        {
                            ...metricData,
                            Weekly: {
                                ...metricData.Weekly,
                                Graph: points.map((point, i) => ({
                                    ...point,
                                    PeriodEnd:
                                        i < points.length - 1
                                            ? dayjs
                                                  .utc(points[i + 1].Key)
                                                  .subtract(1, "day")
                                                  .format("YYYY-MM-DD")
                                            : lastDay,
                                })),
                            },
                        },
                    ];
                }),
            );

            return [deviceKey, enrichedMetrics];
        }),
    ) as IRawData["Graph"];

    return { ...data, Graph: enrichedGraph };
};

const buildQueryParams = (contextParams: HighLevelMetricsParams, categoryObject: ICategory) => {
    const { duration, country, webSource, comparedDuration, includeSubdomains = true } = contextParams;

    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const {
        iso: { from, to, compareFrom, compareTo },
        isWindow,
    } = durationObject.forAPI;

    return {
        from,
        to,
        country,
        isWindow,
        keys: categoryObject.forApi,
        categoryHash: categoryObject.categoryHash,
        webSource,
        compareFrom,
        compareTo,
        includeSubdomains,
    };
};

export const useHighLevelMetricsData = (contextParams: HighLevelMetricsParams, categoryObject: ICategory) => {
    const fetchService = DefaultFetchService.getInstance();
    const queryParams = buildQueryParams(contextParams, categoryObject);
    const isPop = !!contextParams.comparedDuration;

    const shouldFetchUniqueData =
        (!categoryObject.isCustomCategory || categoryObject.segments?.length === 0) && !queryParams.isWindow;

    const queryResult = useQueries({
        queries: [
            {
                queryKey: ["high-level-metrics-traffic", queryParams],
                queryFn: () => {
                    const endpoint = isPop
                        ? TRAFFIC_AND_ENGAGEMENT_ENDPOINTS.OVERVIEW_COMPARE
                        : TRAFFIC_AND_ENGAGEMENT_ENDPOINTS.OVERVIEW;
                    return fetchService.get<IRawData>(endpoint, queryParams);
                },
                select: normalizeGraphData,
            },
            {
                queryKey: ["high-level-metrics-unique-users-overtime", queryParams],
                queryFn: () => {
                    const endpoint = isPop
                        ? UNIQUE_USERS_ENDPOINTS.OVERTIME_PERIOD_OVER_PERIOD
                        : UNIQUE_USERS_ENDPOINTS.OVERTIME;
                    return fetchService.get<IUniqueUsersOvertimeRawData>(endpoint, {
                        ...queryParams,
                        timeGranularity: toggleItems.Monthly.value,
                    });
                },
                enabled: shouldFetchUniqueData,
            },
            {
                queryKey: ["high-level-metrics-unique-users-average", queryParams],
                queryFn: () =>
                    fetchService.get<IUniqueUsersAverageRawData>(UNIQUE_USERS_ENDPOINTS.AVERAGE, {
                        ...queryParams,
                        timeGranularity: toggleItems.Monthly.value,
                    }),
                enabled: shouldFetchUniqueData,
            },
        ],
        combine: (results) => {
            const [trafficResult, overtimeResult, averageResult] = results;
            const isLoading = results.some((r) => r.isLoading);
            const isError = results.some((r) => r.isError);
            const error = results.find((r) => r.error)?.error ?? null;

            const isSuccess = shouldFetchUniqueData
                ? results.every((result) => result.isSuccess)
                : trafficResult.isSuccess;

            if (isLoading || isError || !trafficResult.data) {
                return { data: undefined, isLoading, isError, isSuccess: false, error };
            }

            if (!shouldFetchUniqueData || !overtimeResult.data || !averageResult.data) {
                return {
                    data: enrichWeeklyDataWithPeriodEnd(trafficResult.data, queryParams.to),
                    isLoading,
                    isError,
                    isSuccess,
                    error,
                };
            }

            const merged = mergeUniqueUsersData(trafficResult.data, overtimeResult.data, averageResult.data, isPop);
            return { data: enrichWeeklyDataWithPeriodEnd(merged, queryParams.to), isLoading, isError, isSuccess, error };
        },
    });

    const { isRecovering } = useDurationValidationRecovery(queryResult.error, queryResult.isSuccess);

    return {
        ...queryResult,
        isLoading: queryResult.isLoading || isRecovering,
    };
};
